import { QueryErrorBoundary } from "@components/common/errors";
import { MetricCard, type MetricCardState } from "@components/display/MetricCard";
import { MetricsGrid } from "@components/display/MetricsGrid";
import { StatusBadge, type StatusConfigMap } from "@components/display/StatusBadge";
import { PointageWidget } from "@features/pointage/components/self/PointageWidget";
import { ROUTES } from "@features/rh/config/constants";
import { STATUS as CONGES_STATUS } from "@features/rh/conges/config/statuses";
import { STATUT_FEUILLE } from "@features/rh/feuilles-de-temps/config/statuses";
import { Link } from "@tanstack/react-router";
import {
  Calendar,
  CalendarDays,
  ChevronRight,
  Clock,
  Inbox,
  Plane,
  PlusCircle,
} from "lucide-react";
import { useDashboardSummary } from "../hooks/useDashboardSummary";
import { DashboardSkeletonGrid } from "./SkeletonWidget";

/**
 * DashboardHomeView - Home tab content (metrics, quick actions, recent activity)
 */
export function DashboardHomeView() {
  const { data, isLoading, error, refetch } = useDashboardSummary();

  if (isLoading) return <DashboardSkeletonGrid />;
  if (error)
    return (
      <QueryErrorBoundary
        error={error}
        refetch={refetch}
        className="my-4"
        title="Erreur du tableau de bord"
      />
    );

  const cpBalance = data?.congesPayesRestants ?? 0;
  const cpPris = data?.cpPris ?? 0;
  const cpAcquis = data?.cpAcquis ?? 0;
  const cpPeriode = data?.cpPeriode;
  const heuresSupRestants = data?.heuresSupRestants ?? 0;
  const heuresSupPeriode = data?.heuresSupPeriode;
  const nextConge = data?.nextConge;

  const recentAbsences = Array.isArray(data?.recentAbsences) ? data.recentAbsences : [];
  const recentTimesheets = Array.isArray(data?.recentTimesheets) ? data.recentTimesheets : [];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Metrics Row */}
      <MetricsGrid columns={4} gap="md">
        <MetricCard
          label="Solde Congés Payés"
          state={toMetricState(`${cpBalance} jours`)}
          icon={Calendar}
          accent="info"
          description={cpPeriode}
        />
        <MetricCard
          label="CP Pris / Acquis"
          state={toMetricState(`${cpPris} / ${cpAcquis}`)}
          icon={Calendar}
          accent="positive"
        />
        <MetricCard
          label="Heures Sup."
          state={toMetricState(formatMinutesToHours(heuresSupRestants))}
          icon={PlusCircle}
          accent="attention"
          description={heuresSupPeriode}
        />
        <MetricCard
          label="Prochain Congé"
          state={toMetricState(nextConge ? formatDateShortFull(nextConge.dateDebut) : "Aucun")}
          icon={Plane}
          accent="primary"
        />
      </MetricsGrid>

      {/* Quick Actions */}
      <div
        className="animate-fade-in"
        style={{ animationDelay: "200ms", animationFillMode: "both" }}
      >
        <h2 className="text-xl font-bold text-base-content tracking-tight mb-5 flex items-center gap-3">
          Mes Accès Rapides
          <span className="h-px flex-1 bg-gradient-to-r from-base-300/60 to-transparent ml-4" />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-2xl">
          <QuickAccessCard
            title="Demander un Congé"
            description="Soumettre une demande d'absence"
            icon={Calendar}
            iconBg="bg-info/10"
            iconColor="text-info"
            to={ROUTES.RH.CONGES.NEW_REQUEST}
            delay="0ms"
          />
          <PointageWidget />
        </div>
      </div>

      {/* Recent Activity */}
      <div
        className="animate-fade-in"
        style={{ animationDelay: "300ms", animationFillMode: "both" }}
      >
        <h2 className="text-xl font-bold text-base-content tracking-tight mb-5 flex items-center gap-3">
          Activité Récente
          <span className="h-px flex-1 bg-gradient-to-r from-base-300/60 to-transparent ml-4" />
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Timesheets List */}
          <div className="card bg-base-200 shadow-sm hover:shadow-md transition-shadow duration-300 border border-base-300/60 overflow-hidden rounded-xl">
            <div className="card-body p-0">
              <div className="p-5 md:p-6 border-b border-base-200/60 bg-gradient-to-r from-base-100 to-base-100/50 backdrop-blur-sm flex items-center gap-3">
                <Clock className="w-5 h-5 text-warning" />
                <h3 className="card-title text-base font-bold mb-0">Feuilles de Temps Récentes</h3>
              </div>
              <div className="divide-y divide-base-200/60">
                {recentTimesheets.length > 0 ? (
                  recentTimesheets
                    .slice(0, 5)
                    .map((ts: any) => (
                      <ActivityRow
                        key={ts.id}
                        title={`Semaine du ${formatDate(ts.time)}`}
                        subtitle={`${ts.localisation || "Bureau"} - Validée`}
                        statusCode="VALIDEE"
                        config={STATUT_FEUILLE}
                      />
                    ))
                ) : (
                  <EmptyState message="Aucune feuille de temps récente" />
                )}
              </div>
            </div>
          </div>

          {/* Leaves List */}
          <div className="card bg-base-200 shadow-sm hover:shadow-md transition-shadow duration-300 border border-base-300/60 overflow-hidden rounded-xl">
            <div className="card-body p-0">
              <div className="p-5 md:p-6 border-b border-base-200/60 bg-gradient-to-r from-base-100 to-base-100/50 backdrop-blur-sm flex items-center gap-3">
                <CalendarDays className="w-5 h-5 text-info" />
                <h3 className="card-title text-base font-bold mb-0">
                  Dernières Demandes de Congés
                </h3>
              </div>
              <div className="divide-y divide-base-200/60">
                {recentAbsences.length > 0 ? (
                  recentAbsences.slice(0, 5).map((abs: any) => {
                    const statusKnown = abs.etatCode in CONGES_STATUS;
                    return (
                      <ActivityRow
                        key={abs.id}
                        title={`${abs.type || "Congé"} - ${abs.nombreJours || 0} jours`}
                        subtitle={`${formatDate(abs.dateDebut)} - ${formatDate(abs.dateFin)}`}
                        statusCode={abs.etatCode}
                        config={CONGES_STATUS}
                        label={statusKnown ? undefined : abs.etat}
                      />
                    );
                  })
                ) : (
                  <EmptyState message="Aucune demande de congé récente" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Subcomponents ---

function QuickAccessCard({
  title,
  description,
  icon: Icon,
  iconBg,
  iconColor,
  to,
  delay = "0ms",
}: any) {
  return (
    <Link
      to={to}
      className="card bg-base-200 shadow-sm border border-base-300/60 hover:shadow-lg hover:border-primary/30 hover:-translate-y-1 transition-all duration-300 group animate-fade-in rounded-xl relative overflow-hidden"
      style={{ animationDelay: delay, animationFillMode: "both" }}
    >
      <div className="card-body p-5 md:p-6 flex-row items-center gap-4">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${iconBg} ${iconColor} group-hover:scale-110 transition-transform duration-300 shadow-sm`}
        >
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="card-title text-base mb-0 group-hover:text-primary transition-colors">
            {title}
          </h3>
          <p className="text-sm text-base-content/60 mt-1 leading-snug">{description}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-base-content/30 group-hover:text-primary group-hover:translate-x-1 transition-all duration-300 ease-out" />
      </div>
    </Link>
  );
}

function ActivityRow({
  title,
  subtitle,
  statusCode,
  config,
  label,
}: {
  title: string;
  subtitle: string;
  statusCode?: string;
  config: StatusConfigMap;
  label?: string;
}) {
  // Resolve the L2.5 token for the status dot (same token drives the badge).
  const resolved = config[statusCode ?? ""];
  const dotToken = resolved?.token ?? "draft";

  return (
    <div className="p-4 md:p-5 flex items-center justify-between hover:bg-gradient-to-r hover:from-base-200/50 hover:to-transparent transition-all duration-300 group cursor-default relative">
      {/* Glow effect on hover */}
      <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      <div className="flex items-center gap-4 relative z-10">
        <div
          className="w-2.5 h-2.5 rounded-full shrink-0 shadow-[0_0_8px_currentColor] group-hover:shadow-[0_0_12px_currentColor] group-hover:scale-150 transition-all duration-300 ring-2 ring-base-100 group-hover:ring-transparent"
          style={{
            backgroundColor: `var(--status-${dotToken})`,
            color: `var(--status-${dotToken})`,
          }}
        />
        <div>
          <p className="font-semibold text-sm text-base-content group-hover:text-primary transition-colors duration-300">
            {title}
          </p>
          <p className="text-xs text-base-content/60 mt-0.5">{subtitle}</p>
        </div>
      </div>
      <StatusBadge
        status={statusCode ?? ""}
        config={config}
        label={label}
        className="px-3 py-2 font-medium shadow-sm group-hover:shadow-md transition-shadow duration-300"
      />
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="p-8 flex flex-col items-center justify-center text-center">
      <div className="w-12 h-12 rounded-full bg-base-200/50 flex items-center justify-center mb-3">
        <Inbox className="w-6 h-6 text-base-content/30" />
      </div>
      <p className="text-sm font-medium text-base-content/60">{message}</p>
      <p className="text-xs text-base-content/40 mt-1">Les nouvelles activités apparaîtront ici</p>
    </div>
  );
}

// --- Helpers ---

/** Wraps a string value into a MetricCard "ready" state. */
function toMetricState(value: string | number): MetricCardState {
  return { type: "ready", value };
}

export function formatDate(dateString: string) {
  if (!dateString) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(dateString));
  } catch {
    return dateString;
  }
}

export function formatDateShort(dateString: string) {
  if (!dateString) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "short",
    }).format(new Date(dateString));
  } catch {
    return dateString;
  }
}

export function formatDateShortFull(dateString: string) {
  if (!dateString) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(dateString));
  } catch {
    return dateString;
  }
}

export function formatMinutesToHours(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
}
